// ===========================================================================
// Emoji: the compose picker, and the fixed reaction set.
//
// Two different things, deliberately kept apart:
//
//   * PICKER emoji are typed into the message box. They are ordinary text and
//     go through the text moderator like any other characters, so the list can
//     be as long as is useful.
//   * REACTION emoji reach the other person WITHOUT passing the moderator, so
//     that set is small, fixed, and defined in the shared protocol where the
//     server validates against it.
//
// The picker is the same one in the 1:1 chat, the group room and a private
// message thread (ui/messages.js), and it works with an input or a textarea.
// The ones a person has picked lately sit at the top (this device only, in
// localStorage), which on a phone is most of what anyone scrolls for.
//
// And it can be searched by name (ui/emoji-names.js): "heart", "lol", "cat",
// "thumbs up" (the operator, 2026-10-07).
//
// Emoji are written as CODE POINTS, not literals, to keep this source ASCII.
// Nothing that is a weapon is offered; everything else that people reach for
// in a chat is (the operator, 2026-09-29: "add more emojis").
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';
import { REACTION_EMOJI } from '@lumi/shared';
import { EMOJI_NAMES } from './emoji-names.js';

const cp = (...points) => String.fromCodePoint(...points);
const V = 0xfe0f; // the emoji presentation selector, for the ones that need it

/** The groups the picker shows, in this order. Exported for the test that counts them. */
export const EMOJI_GROUPS = [
  {
    name: 'Smileys',
    points: [
      [0x1f600], [0x1f603], [0x1f604], [0x1f601], [0x1f606], [0x1f605], [0x1f923], [0x1f602],
      [0x1f642], [0x1f643], [0x1f609], [0x1f60a], [0x1f607], [0x1f970], [0x1f60d], [0x1f929],
      [0x1f618], [0x1f617], [0x1f61a], [0x1f619], [0x1f60b], [0x1f61b], [0x1f61c], [0x1f92a],
      [0x1f61d], [0x1f911], [0x1f917], [0x1f92d], [0x1f92b], [0x1f914], [0x1f910], [0x1f928],
      [0x1f610], [0x1f611], [0x1f636], [0x1f60f], [0x1f612], [0x1f644], [0x1f62c], [0x1f925],
      [0x1f60c], [0x1f614], [0x1f62a], [0x1f924], [0x1f634], [0x1f637], [0x1f912], [0x1f915],
      [0x1f922], [0x1f92e], [0x1f927], [0x1f975], [0x1f976], [0x1f974], [0x1f635], [0x1f92f],
      [0x1f920], [0x1f973], [0x1f60e], [0x1f913], [0x1f9d0],
    ],
  },
  {
    name: 'Feelings',
    points: [
      [0x1f615], [0x1f61f], [0x1f641], [0x1f62e], [0x1f62f], [0x1f632], [0x1f633], [0x1f97a],
      [0x1f626], [0x1f627], [0x1f628], [0x1f630], [0x1f625], [0x1f622], [0x1f62d], [0x1f631],
      [0x1f616], [0x1f623], [0x1f61e], [0x1f613], [0x1f629], [0x1f62b], [0x1f971], [0x1f624],
      [0x1f621], [0x1f620], [0x1f92c], [0x1f608], [0x1f47f], [0x1f480], [0x1f4a9], [0x1f921],
      [0x1f47b], [0x1f47d], [0x1f916], [0x1f63a], [0x1f638], [0x1f639], [0x1f63b], [0x1f63c],
      [0x1f63d], [0x1f640], [0x1f63f], [0x1f63e],
    ],
  },
  {
    name: 'Hands',
    points: [
      [0x1f44b], [0x1f91a], [0x1f590, V], [0x270b], [0x1f596], [0x1f44c], [0x1f90c], [0x1f90f],
      [0x270c, V], [0x1f91e], [0x1f91f], [0x1f918], [0x1f919], [0x1f448], [0x1f449], [0x1f446],
      [0x1f447], [0x261d, V], [0x1f44d], [0x1f44e], [0x270a], [0x1f44a], [0x1f91b], [0x1f91c],
      [0x1f44f], [0x1f64c], [0x1f450], [0x1f932], [0x1f91d], [0x1f64f], [0x270d, V], [0x1f485],
      [0x1f933], [0x1f4aa], [0x1f9b5], [0x1f442], [0x1f443], [0x1f440], [0x1f441, V], [0x1f445],
      [0x1f444], [0x1f9e0],
    ],
  },
  {
    name: 'People',
    points: [
      [0x1f476], [0x1f9d2], [0x1f466], [0x1f467], [0x1f9d1], [0x1f468], [0x1f469], [0x1f9d3],
      [0x1f474], [0x1f475], [0x1f46e], [0x1f477], [0x1f478], [0x1f934], [0x1f470], [0x1f935],
      [0x1f9b8], [0x1f9b9], [0x1f9d9], [0x1f9da], [0x1f9dd], [0x1f483], [0x1f57a], [0x1f46b],
      [0x1f46c], [0x1f46d], [0x1f48f], [0x1f491], [0x1f46a], [0x1f5e3, V], [0x1f464], [0x1f465],
    ],
  },
  {
    name: 'Hearts and signs',
    points: [
      [0x2764, V], [0x1f9e1], [0x1f49b], [0x1f49a], [0x1f499], [0x1f49c], [0x1f90e], [0x1f5a4],
      [0x1f90d], [0x1f494], [0x2763, V], [0x1f495], [0x1f49e], [0x1f493], [0x1f497], [0x1f496],
      [0x1f498], [0x1f49d], [0x1f49f], [0x262e, V], [0x1f4af], [0x1f4a2], [0x1f4a5], [0x1f4ab],
      [0x1f4a6], [0x1f4a8], [0x1f4ac], [0x1f4ad], [0x1f4a4], [0x2728], [0x2b50], [0x1f31f],
      [0x26a1], [0x1f525], [0x1f308], [0x2705], [0x274c], [0x2753], [0x2757], [0x203c, V],
      [0x2049, V], [0x1f51e], [0x1f4f5], [0x1f3b5], [0x1f3b6], [0x1f4a1],
    ],
  },
  {
    name: 'Animals and nature',
    points: [
      [0x1f436], [0x1f431], [0x1f42d], [0x1f439], [0x1f430], [0x1f98a], [0x1f43b], [0x1f43c],
      [0x1f428], [0x1f42f], [0x1f981], [0x1f42e], [0x1f437], [0x1f438], [0x1f435], [0x1f412],
      [0x1f414], [0x1f427], [0x1f426], [0x1f424], [0x1f986], [0x1f985], [0x1f989], [0x1f987],
      [0x1f43a], [0x1f417], [0x1f434], [0x1f984], [0x1f41d], [0x1f41b], [0x1f98b], [0x1f40c],
      [0x1f41e], [0x1f41c], [0x1f422], [0x1f40d], [0x1f98e], [0x1f419], [0x1f991], [0x1f990],
      [0x1f980], [0x1f421], [0x1f420], [0x1f41f], [0x1f42c], [0x1f433], [0x1f40b], [0x1f988],
      [0x1f40a], [0x1f405], [0x1f406], [0x1f993], [0x1f98d], [0x1f418], [0x1f98f], [0x1f42a],
      [0x1f42b], [0x1f992], [0x1f998], [0x1f411], [0x1f410], [0x1f98c], [0x1f415], [0x1f429],
      [0x1f408], [0x1f413], [0x1f983], [0x1f54a, V], [0x1f407], [0x1f43f, V], [0x1f409], [0x1f432],
      [0x1f335], [0x1f384], [0x1f332], [0x1f333], [0x1f334], [0x1f331], [0x1f33f], [0x2618, V],
      [0x1f340], [0x1f341], [0x1f342], [0x1f343], [0x1f33a], [0x1f33b], [0x1f33c], [0x1f337],
      [0x1f338], [0x1f339], [0x1f33e], [0x1f30d], [0x1f30e], [0x1f30f], [0x1f311], [0x1f315],
      [0x1f319], [0x1f31e], [0x2600, V], [0x26c5], [0x2601, V], [0x1f327, V], [0x26c8, V],
      [0x1f328, V], [0x2744, V], [0x26c4], [0x1f32c, V], [0x1f30a],
    ],
  },
  {
    name: 'Food and drink',
    points: [
      [0x1f34f], [0x1f34e], [0x1f34a], [0x1f34b], [0x1f34c], [0x1f349], [0x1f347], [0x1f353],
      [0x1f352], [0x1f351], [0x1f34d], [0x1f95d], [0x1f345], [0x1f965], [0x1f951], [0x1f346],
      [0x1f955], [0x1f33d], [0x1f336, V], [0x1f954], [0x1f35e], [0x1f9c0], [0x1f95a], [0x1f373],
      [0x1f953], [0x1f357], [0x1f354], [0x1f35f], [0x1f355], [0x1f32d], [0x1f96a], [0x1f32e],
      [0x1f32f], [0x1f957], [0x1f35d], [0x1f35c], [0x1f363], [0x1f371], [0x1f35a], [0x1f358],
      [0x1f359], [0x1f362], [0x1f366], [0x1f368], [0x1f370], [0x1f382], [0x1f36b], [0x1f36c],
      [0x1f36d], [0x1f369], [0x1f36a], [0x1f37f], [0x2615], [0x1f375], [0x1f9c3], [0x1f964],
      [0x1f9cb], [0x1f37a], [0x1f377], [0x1f378], [0x1f379], [0x1f942],
    ],
  },
  {
    name: 'Activities and places',
    points: [
      [0x26bd], [0x1f3c0], [0x1f3c8], [0x26be], [0x1f3be], [0x1f3d0], [0x1f3c9], [0x1f3b1],
      [0x1f3d3], [0x1f3f8], [0x1f945], [0x26f3], [0x1f3f9], [0x1f3a3], [0x1f94a], [0x1f3bf],
      [0x1f6f7], [0x1f3c2], [0x1f3c4], [0x1f6a3], [0x1f3ca], [0x1f6b4], [0x1f3c6], [0x1f947],
      [0x1f948], [0x1f949], [0x1f3ab], [0x1f3ad], [0x1f3a8], [0x1f3ac], [0x1f3a4], [0x1f3a7],
      [0x1f3b8], [0x1f3b9], [0x1f3ba], [0x1f3bb], [0x1f941], [0x1f3ae], [0x1f579, V], [0x1f3b2],
      [0x1f9e9], [0x265f, V], [0x1f697], [0x1f695], [0x1f68c], [0x1f3ce, V], [0x1f3cd, V], [0x1f6b2],
      [0x1f6f5], [0x1f6a8], [0x1f682], [0x1f686], [0x2708, V], [0x1f680], [0x1f6f8], [0x26f5],
      [0x1f6a2], [0x1f3e0], [0x1f3e2], [0x1f3e5], [0x1f3eb], [0x1f3f0], [0x1f5fc], [0x1f5fd],
      [0x1f3d6, V], [0x1f3dd, V], [0x26fa], [0x1f3d5, V], [0x1f3d4, V], [0x1f303], [0x1f309], [0x1f386],
    ],
  },
  {
    name: 'Things',
    points: [
      [0x231a], [0x1f4f1], [0x1f4bb], [0x2328, V], [0x1f5a8, V], [0x1f4f7], [0x1f4f8], [0x1f4f9],
      [0x1f3a5], [0x1f4fa], [0x1f4fb], [0x1f4fc], [0x1f4bf], [0x1f4be], [0x1f4bd], [0x1f4d5],
      [0x1f4d6], [0x1f4da], [0x1f4dd], [0x1f4f0], [0x1f4c5], [0x1f4cc], [0x1f4ce], [0x1f4cf],
      [0x1f4d0], [0x1f4d2], [0x1f4d3], [0x2702, V], [0x1f512], [0x1f513], [0x1f511], [0x1f528],
      [0x1f527], [0x1f529], [0x1f52c], [0x1f52d], [0x1f526], [0x1f56f, V], [0x1f4b0], [0x1f4b3],
      [0x1f48e], [0x1f381], [0x1f388], [0x1f389], [0x1f38a], [0x1f380], [0x1f9f8], [0x1f9f9],
      [0x1f9fa], [0x1f6cf, V], [0x1f6bf], [0x1f6c1], [0x1f9f4], [0x1f9fc], [0x1f393], [0x1f451],
      [0x1f452], [0x1f3a9], [0x1f392], [0x1f45c], [0x1f45d], [0x1f45e], [0x1f45f], [0x1f460],
      [0x1f461], [0x1f462], [0x1f97e], [0x1f97f], [0x1f455], [0x1f456], [0x1f457], [0x1f458],
      [0x1f459], [0x1f45a], [0x1f9e3], [0x1f9e4], [0x1f9e5], [0x1f9e6], [0x1f48d], [0x1f48c],
      [0x1f4e3], [0x1f4ef], [0x1f4e2], [0x1f514],
    ],
  },
];

// ---- Searching by name -------------------------------------------------------------

/** The most a search shows at once: four rows on a phone is plenty to pick from. */
export const SEARCH_MAX = 48;

/**
 * The emoji whose name, everyday words or group fit what was typed: every
 * word typed must begin a word of theirs, so "heart" finds every heart and
 * "thumb up" finds the thumbs up, and "cat" does not find "education". In
 * the picker's own order.
 * @returns {string[]}
 */
export function searchEmoji(query, groups = EMOJI_GROUPS, max = SEARCH_MAX) {
  const terms = String(query || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).slice(0, 6);
  if (!terms.length) return [];
  const out = [];
  for (const group of groups) {
    const groupWords = group.name.toLowerCase();
    for (const points of group.points) {
      const words = `${EMOJI_NAMES[points[0].toString(16)] || ''} ${groupWords}`.split(/[^a-z0-9]+/).filter(Boolean);
      if (terms.every((term) => words.some((w) => w.startsWith(term)))) out.push(cp(...points));
      if (out.length >= max) return out;
    }
  }
  return out;
}

// ---- The ones picked lately, on this device --------------------------------------

const RECENT_KEY = 'lumi.emoji.recent';
/** How many recently picked emoji the top row keeps: two rows on a phone. */
export const RECENT_MAX = 16;

/** The emoji this device picked lately, most recent first. Never throws. */
export function recentEmoji() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((x) => typeof x === 'string' && x).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

/** One more picked: to the front, once, the list cut to RECENT_MAX. Returns the list. */
export function rememberEmoji(emoji) {
  if (typeof emoji !== 'string' || !emoji) return recentEmoji();
  const next = [emoji, ...recentEmoji().filter((x) => x !== emoji)].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable: the row is empty next time, nothing else */
  }
  return next;
}

/**
 * A popover of emoji that inserts into a text input or a textarea.
 *
 * @param {HTMLInputElement|HTMLTextAreaElement} input the message box to type into
 * @returns {{ button: HTMLElement, panel: HTMLElement, close: Function }}
 */
export function createEmojiPicker(input) {
  const panel = el('div', { class: 'emoji-panel hidden' });

  // Recently picked, redrawn on every open and every pick.
  const recentName = el('div', { class: 'emoji-group-name', text: t('Recent') });
  const recentGrid = el('div', { class: 'emoji-grid emoji-recent' });
  recentName.hidden = true;
  recentGrid.hidden = true;
  const pick = (emoji) => {
    insertAtCaret(input, emoji);
    drawRecent(rememberEmoji(emoji));
  };
  const emojiButton = (emoji) => el('button', {
    type: 'button',
    class: 'emoji-btn',
    text: emoji,
    // Insert AT THE CARET rather than appending, so picking an emoji
    // mid-sentence does what it looks like it will do.
    onClick: () => pick(emoji),
  });
  function drawRecent(list) {
    recentGrid.replaceChildren(...list.map(emojiButton));
    // Not while a search is showing what it found.
    const shown = list.length > 0 && !(search && search.value.trim());
    recentName.hidden = !shown;
    recentGrid.hidden = !shown;
  }
  // Search by name. What it finds replaces the groups while there is
  // something typed; Enter picks the first, Escape clears it (then closes).
  const search = el('input', {
    type: 'search',
    class: 'emoji-search',
    placeholder: t('Search emoji'),
    'aria-label': 'Search emoji',
    autocomplete: 'off',
    maxlength: '40',
  });
  const foundGrid = el('div', { class: 'emoji-grid emoji-found' });
  const noneFound = el('div', { class: 'emoji-none muted', text: t('No emoji by that name.') });
  foundGrid.hidden = true;
  noneFound.hidden = true;
  const browsing = [];
  let found = [];
  function drawSearch() {
    const q = search.value.trim();
    found = q ? searchEmoji(q) : [];
    foundGrid.replaceChildren(...found.map(emojiButton));
    foundGrid.hidden = !q || !found.length;
    noneFound.hidden = !q || found.length > 0;
    for (const node of browsing) node.hidden = !!q;
    drawRecent(recentEmoji());
  }
  search.addEventListener('input', drawSearch);
  search.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (found.length) pick(found[0]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (search.value) {
        search.value = '';
        drawSearch();
      } else {
        close();
        input.focus();
      }
    }
  });
  panel.append(search, foundGrid, noneFound, recentName, recentGrid);

  for (const group of EMOJI_GROUPS) {
    const name = el('div', { class: 'emoji-group-name', text: group.name });
    const grid = el('div', { class: 'emoji-grid' }, group.points.map((points) => emojiButton(cp(...points))));
    browsing.push(name, grid);
    panel.append(name, grid);
  }

  const close = () => panel.classList.add('hidden');
  const button = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-sm emoji-toggle',
    title: t('Insert an emoji'),
    'aria-label': 'Insert an emoji',
    text: cp(0x1f642),
    onClick: (e) => {
      e.stopPropagation();
      panel.classList.toggle('hidden');
      if (!panel.classList.contains('hidden')) {
        // Every open starts from the groups, not from the last search.
        search.value = '';
        drawSearch();
        input.focus();
      }
    },
  });

  // Clicking anywhere else puts it away, the way a menu should behave. (A
  // test's document may have no listeners to add; the picker still works.)
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('click', (e) => {
      if (!panel.contains(e.target) && e.target !== button) close();
    });
  }

  return { button, panel, close };
}

function insertAtCaret(input, text) {
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  input.value = input.value.slice(0, start) + text + input.value.slice(end);
  const caret = start + text.length;
  input.setSelectionRange?.(caret, caret);
  input.focus();
  // Let anything listening (typing indicator, send-button state, a textarea
  // that grows with its text) notice.
  input.dispatchEvent?.(new Event('input', { bubbles: true }));
}

/**
 * The row of reaction choices shown when someone reacts to a message.
 * @param {(emoji: string) => void} onPick
 */
export function createReactionBar(onPick) {
  return el(
    'div',
    { class: 'react-bar' },
    REACTION_EMOJI.map((emoji) =>
      el('button', {
        type: 'button',
        class: 'react-choice',
        text: emoji,
        title: `React ${emoji}`,
        onClick: (e) => {
          e.stopPropagation();
          onPick(emoji);
        },
      }),
    ),
  );
}

export default { createEmojiPicker, createReactionBar, EMOJI_GROUPS, recentEmoji, rememberEmoji, RECENT_MAX, searchEmoji, SEARCH_MAX };
